import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { IBoardingTrip } from "../interfaces/endpoints/IBoarding.types";
import { FollowedBus, readFollowedBus, storeFollowedBus } from "../pages/scan/scanHelpers";
import { getBoardingTrips, getDepartures } from "../services/Boarding.service";

// A bus set by its first ticket is let go half an hour after it leaves, so the next run, or the same bus
// further along the route, is found again by its next ticket. A bus picked by hand stays longer for a late
// departure, and stays pickable that long.
const RELEASE_AFTER_MINUTES = 30;
const PICKED_KEEP_MINUTES = 180;
const TICK_MS = 30000;
const RETRY_MS = 15000;
const BOOKED_REFRESH_MS = 60000;

const departureOn = (trip: IBoardingTrip, day: string) => {
  const [hours, minutes] = trip.departureTime.split(":").map(Number);
  return dayjs(day).hour(hours).minute(minutes).second(0);
};

export const isOpen = (trip: IBoardingTrip, now: Dayjs = dayjs()) =>
  !now.isAfter(departureOn(trip, now.format("YYYY-MM-DD")).add(PICKED_KEEP_MINUTES, "minute"));

export const stillCurrent = (followed: FollowedBus | null, trips: IBoardingTrip[], now: Dayjs = dayjs()) => {
  const trip = followed && trips.find((candidate) => candidate.tripId === followed.tripId);
  if (!followed || !trip) return null;
  const keep = followed.setBy ? RELEASE_AFTER_MINUTES : PICKED_KEEP_MINUTES;
  return now.isAfter(departureOn(trip, followed.day).add(keep, "minute")) ? null : trip;
};

// "Nisja" lists today's departures with passengers, and the followed bus. Every trip stays known, so a
// passenger boarding an earlier bus of the line can still set it.
export const pickable = (open: IBoardingTrip[], booked: Set<number> | null, bus: IBoardingTrip | null) =>
  booked ? open.filter((trip) => booked.has(trip.tripId) || trip.tripId === bus?.tripId) : open;

// The bus this phone is scanning for. Nobody has to pick it: the first ticket that boards sets it, and
// "Nisja" can set it by hand or hand it back to the next ticket.
export const useScanTrips = () => {
  const [trips, setTrips] = useState<IBoardingTrip[] | null>(null);
  const [followed, setFollowed] = useState<FollowedBus | null>(readFollowedBus);
  const [now, setNow] = useState(dayjs);
  const [load, setLoad] = useState(0);
  const [booked, setBooked] = useState<Set<number> | null>(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(dayjs()), TICK_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (trips) return;
    let retry: ReturnType<typeof setTimeout>;
    getBoardingTrips()
      .then((response) => setTrips(response.data ?? []))
      .catch(() => {
        if (load === 0) toast.error("Nisjet nuk u ngarkuan");
        retry = setTimeout(() => setLoad((value) => value + 1), RETRY_MS);
      });
    return () => clearTimeout(retry);
  }, [trips, load]);

  useEffect(() => {
    const refresh = () =>
      getDepartures(dayjs().format("YYYY-MM-DD"))
        .then((response) => setBooked(new Set((response.data ?? []).map((departure) => departure.tripId))))
        .catch(() => undefined);
    void refresh();
    const timer = setInterval(refresh, BOOKED_REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  const known = useMemo(() => trips ?? [], [trips]);
  const bus = useMemo(() => stillCurrent(followed, known, now), [followed, known, now]);
  const openTrips = useMemo(
    () => pickable(known.filter((trip) => isOpen(trip, now)), booked, bus),
    [known, now, booked, bus]
  );

  const set = (next: FollowedBus | null) => {
    setFollowed(next);
    storeFollowedBus(next);
  };
  const day = () => dayjs().format("YYYY-MM-DD");

  return {
    trips: openTrips,
    bus,
    busDay: bus ? followed?.day : undefined,
    choose: (tripId: number | null) => set(tripId ? { tripId, day: day() } : null),
    follow: (tripId: number, attemptId?: number | null) =>
      set({ tripId, day: day(), setBy: attemptId ?? undefined }),
    // Undoing the scan that set the bus also forgets the bus: that first passenger was often on another one.
    release: (attemptId: number) => {
      if (followed?.setBy === attemptId) set(null);
    }
  };
};

export default useScanTrips;
